import { BaseControl, IControlParameters } from '@Common/Controls/BaseControl';
import { IBaseMenuNode, selectMode } from '@Common/Controls/BaseMenu';
import { IMargins, IValueChange } from '@Common/Controls/Controls';
import { Popup } from '@Common/Controls/Popup';
import constants from '@Common/Core/Constant';
import { destroyEvents, safeDestroys } from '@Common/Core/Destroyable';
import { MulticastDelegate } from '@Common/Core/MulticastDelegate';
import { properties } from '@Common/Core/Properties';
import pointerHelper from '@Common/Helpers/PointerHelper';
import StylesHelper from '@Common/Helpers/StylesHelper';
import { IListParameters, List } from './List';

export interface IDropDownParameters extends IControlParameters {
    height?: number;
    isTree?: boolean;
    noSelectionText?: string;
    selectMode?: string;
    showClearAllButton?: boolean;
    showSetButton?: boolean;
    isDisable?: boolean;
    isComponent?: boolean;
    titleStyle?: string;
    contentStyle?: string;
    itemDefaultStyle?: string;
    itemHoverStyle?: string;
    itemSelectedStyle?: string;
    margins?: IMargins;
    onAppendData?: () => void;
    onShowContent?: () => void;
    searchPanel?: boolean;
    groupsButton?: boolean;
    onGroupsButtonClick?: () => void;
    onSearch?: (arg: { operatorType: string; values: any[] }) => void;
    maxContentHeight?: number;
    showControls?: boolean;
    resizable?: boolean;
    comparer?: (item1: any, item2: any) => number;
    checkable?: boolean;
    sortable?: boolean;
    getTitleDescription?: (items: any[]) => string;
    getNoSelectionText?: () => string;
    titleDescriptionIsHtml?: boolean;
    isSimpleCheckMode?: boolean;
    checkOnClick?: boolean;
    selectOnCheck?: boolean;
    unselectOnClick?: boolean;
    caption?: string;
    idPropertyName?: string;
    namePropertyName?: string;
    stylesHelper?: StylesHelper;
    minWidth?: number;
    visibilityComparer?: (data: any) => boolean;
    canSwitchToExcludedMode?: boolean;
    excludeMode?: boolean;
}

export class DropDown extends BaseControl implements IDropDown {
    public onValidate = MulticastDelegate.create();
    public onChange = MulticastDelegate.create<ICallback<{ id: any; name: string; [key: string]: any }[]>>();
    private _lastSelectedItems: any[];
    private _isContentVisible = false;

    private _onItemClick = _.noop;
    private _onAppendItems = _.noop;
    private _onShowContent = _.noop;
    private _onRemoveContent = _.noop;
    private _list: List;
    private _titleControl: JQuery;
    private _content: JQuery;
    private _popup: Popup;
    private _listControl: JQuery;
    private _initialSelection: any[];
    private _scrollTo: any;

    constructor(param?: IDropDownParameters) {
        super(param);

        this._param = $.extend(
            {
                css: null,
                className: '',
                width: '100%',
                height: 30,
                noSelectionText: '',
                selectMode: selectMode.simple,
                isDesktop: properties.isDesktopBrowser,
                showClearAllButton: false,
                showSetButton: false,
                isDisable: false,
                isComponent: false,
                titleStyle: '',
                contentStyle: constants.driverDefaultStyle.contentStyle,
                itemDefaultStyle: constants.driverDefaultStyle.itemDefaultStyle,
                itemHoverStyle: constants.driverDefaultStyle.itemHoverStyle,
                itemSelectedStyle: constants.driverDefaultStyle.itemSelectedStyle,
                itemDefaultBorderStyle: constants.driverDefaultStyle.itemDefaultBorderStyle,
                itemHoverBorderStyle: constants.driverDefaultStyle.itemHoverBorderStyle,
                itemSelectedBorderStyle: constants.driverDefaultStyle.itemSelectedBorderStyle,
                margins: {},
                onAppendData: $.noop,
                onShowContent: $.noop,
                searchPanel: false,
                groupsButton: false,
                onGroupsButtonClick: $.noop,
                onSearch: $.noop,
                maxContentHeight: 350,
                showControls: false,
                resizable: false,
                compare: undefined,
                validator: undefined,
                checkable: false,
                sortable: true,
                cloneItems: true,
                getTitleDescription: null,
                getNoSelectionText: null,
                minWidth: null,
                maxWidth: null,
                contentWidth: null,
                titleDescriptionIsHtml: false,
                actionButtonsAppearance: null,
                actionPanelStyle: null,
                searchImageStyle: null,
                triangleImageStyle: null,
                stylesHelper: null,
                canSwitchToExcludedMode: false,
                excludeMode: false
            },
            param
        );

        this._param.margins = $.extend(
            {
                left: false,
                right: false,
                top: false,
                bottom: false
            },
            this._param.margins
        );

        this._list = new List({
            width: '100%',
            height: '100%',
            ignoreKeys: !this._param.checkable,
            selectMode: this._param.selectMode,
            isSimpleCheckMode: this._param.isSimpleCheckMode,
            itemDefaultStyle: this._param.itemDefaultStyle,
            itemHoverStyle: this._param.itemHoverStyle,
            itemSelectedStyle: this._param.itemSelectedStyle,
            itemDefaultBorderStyle: this._param.itemDefaultBorderStyle,
            itemHoverBorderStyle: this._param.itemHoverBorderStyle,
            itemSelectedBorderStyle: this._param.itemSelectedBorderStyle,
            showControls: this._param.showControls,
            onAppendData: this._param.onAppendData,
            actions: {
                clearButton: this._param.showClearAllButton,
                setButton: this._param.showSetButton,
                searchPanel: this._param.searchPanel,
                onSearch: this._param.onSearch,
                onSet: () => {
                    this.removeContent();
                    this.processSelection();
                },
                groupsButton: this._param.groupsButton,
                onGroupsButtonClick: this._param.onGroupsButtonClick,
                actionButtonsAppearance: this._param.actionButtonsAppearance,
                actionPanelStyle: this._param.actionPanelStyle,
                searchImageStyle: this._param.searchImageStyle
            },
            possibleRightAlign: true,
            comparer: this._param.comparer || undefined,
            checkable: this._param.checkable,
            checkOnClick: this._param.checkOnClick,
            selectOnCheck: this._param.selectOnCheck,
            unselectOnClick: this._param.unselectOnClick,
            sortable: this._param.sortable,
            cloneItems: this._param.cloneItems,
            idPropertyName: this._param.idPropertyName || undefined,
            namePropertyName: this._param.namePropertyName || undefined,
            lazyLoad: this._param.lazyLoad || undefined,
            scrollStepPackage: this._param.scrollStepPackage || undefined,
            addItemsUntillScroll: this._param.addItemsUntillScroll || undefined,
            stylesHelper: this._param.stylesHelper,
            visibilityComparer: this._param.visibilityComparer || undefined,
            canSwitchToExcludedMode: this._param.canSwitchToExcludedMode,
            excludeMode: this._param.excludeMode
        } as IListParameters);

        if (this._param.checkable) {
            this._list.onCheckedChanged((oldItems, newItems, clickParam) => {
                this.clickProcessing(oldItems, newItems, clickParam);
            });
        } else {
            this._list.onSelectionChanged((oldItems, newItems, clickParam) => {
                this.clickProcessing(oldItems, newItems, clickParam);
            });
        }

        this._list.onItemClick(item => {
            // remove content by click for simple mode
            !this._param.checkable && item.selectable !== false && this.removeContent();
            this._onItemClick(item);
        });

        this._titleControl = $('<div class="name" unselectable="on"/>');
        this._root = $('<div class="drop-down" unselectable="on"/>')
            .append(this._titleControl)
            .append('<span class="dropdown-arrow" unselectable="on"/>')
            .toggleClass('multiple', this._param.selectMode != selectMode.simple)
            .toggleClass('single', this._param.selectMode == selectMode.simple)
            .addClass(this._param.titleStyle)
            .css({ height: this._param.height + 'px', lineHeight: this._param.height + 'px', width: this._param.width })
            .find('.dropdown-arrow')
            .addClass(this._param.triangleImageStyle)
            .end();

        this.setState(!this._param.isDisable);

        if (this._param.isComponent) {
            this._root.addClass('component-drop-down');
        }
        const rootBorderHeight = this._root.borderHeight();
        const nameBlockHeight = this._param.height - rootBorderHeight + 'px';
        this._titleControl.css({ height: nameBlockHeight, lineHeight: nameBlockHeight });
        //commonUtils.applyControlMargins(this._param.margins, this._root);

        this._root.css('width', this._param.width);
        this._param.className && this._root.addClass(this._param.className);
        this._param.css && this._root.css(this._param.css);

        this._root
            .on(pointerHelper.pointerDownEventName, () => {
                this._isContentVisible = this._content.is(':visible');
            })
            .on('click', e => {
                this._clickHandler(e);
            });

        this._content = $('<div class="drop-down-content popup-shadow"/>')
            .css({
                top: this._param.lineHeight,
                position: 'absolute',
                maxHeight: this._param.maxContentHeight,
                minWidth: this._param.minWidth
            })
            .addClass(this._param.contentStyle)
            .on('removePopup', () => {
                if (this._param.showSetButton) {
                    this.restoreSelection();
                }
                this.removeContent();
            });

        this._param.maxWidth && this._content.css({ maxWidth: this._param.maxWidth });

        this._popup = new Popup({
            padding: 0,
            content: this._content,
            isHidden: true,
            jointHeight: this._param.height
        });

        this._popup.onResize(size => this._list.adjust(null, this.getHeight(size.height)));
        this._listControl = this._list.getControl().appendTo(this._content);

        this._initialSelection = [];

        this._init();
    }

    _clickHandler(e) {
        if (this._enabled && this._readonly !== true) {
            if (!this._isContentVisible) {
                this.showContent();

                // scroll to
                if (this._scrollTo) {
                    this._list.scrollToItem(this._scrollTo);
                    this._scrollTo = null;
                }
            } else if (this._content.is(':visible')) {
                this.restoreSelection();
                this.removeContent();
            }
        }
    }

    private showContent(): void {
        this._initialSelection = this._param.checkable ? this._list.checkedItems() : this._list.selectedItems();

        this._root.addClass('clicked');
        // Show popup and set dimensions
        const { left, top } = this._root.offset();
        this._popup.show(left, top);

        let actionHeight = 0;
        if (this._list._actions) {
            actionHeight = this._list._actions.getControl().outerHeight(true);
        }
        const [width, height] = [this.getWidth(), this._list.getRootULHeight() + actionHeight];

        this._list.adjust(width, height);
        this._popup.show(left, top, width, this.getHeight(height));
        if (this._initialSelection && this._initialSelection.length) {
            this.scrollToItem(this._initialSelection[0]);
        }
        this._param.onShowContent();
        this._onShowContent();
    }

    getHeight(height?) {
        return Math.min(
            this._param.maxContentHeight,
            height ? height : this._list.getActionsHeight() + this._list.getScrollWrapper().children().outerHeight()
        );
    }

    getMaxContentHeight() {
        return this._param.maxContentHeight;
    }

    getWidth() {
        if (this._param.contentWidth) {
            return this._param.contentWidth;
        }
        let contentWidth = this.getContentWidth();
        contentWidth = Math.max(this._param.maxWidth || 0, contentWidth);
        contentWidth = Math.min(this._param.minWidth || Number.MAX_SAFE_INTEGER, contentWidth);
        return contentWidth;
    }

    private getContentWidth(): number {
        const contents = [this._content, this._list.getControl()];
        const storedWidths = [this._list.getControl(), this._content].map(c => c.css('width'));
        try {
            contents.forEach(c => c.width('auto'));
            return Math.max(this._content.outerWidth(), this._root.outerWidth());
        } finally {
            _.zip(contents, storedWidths).forEach(([c, w]) => c.width(w));
        }
    }

    removeContent() {
        this._popup.hide();
        this._root.removeClass('clicked');
        this._onRemoveContent();
    }

    processSelection() {
        const selectedItems = this._param.checkable ? this._list.checkedItems() : this._list.selectedItems();
        this._lastSelectedItems = selectedItems;
        this.validate();
        this.onChange(selectedItems);
    }

    clickProcessing(oldItems, newItems, clickParam) {
        clickParam = $.extend({ removePopup: false }, clickParam);

        //var checkedItems = list.checkedItems();
        this.setTitle(newItems);

        if (!this._param.checkable && clickParam.removePopup) {
            this.removeContent();
            this.processSelection();
        }
        if (this._param.checkable && !this._param.showSetButton) {
            this.processSelection();
        }
    }

    restoreSelection() {
        if (this._param.checkable) {
            this._list.checkedItems(this._initialSelection);
        } else {
            this._list.selectedItems(this._initialSelection);
        }

        this.setTitle(this._initialSelection);
    }

    setTitle(selectedItems) {
        if (
            selectedItems.length == 0 ||
            (selectedItems[0].id == constants.noFilterId && (selectedItems[0].name ? selectedItems[0].name == constants.noFilterId : true))
        ) {
            this._titleControl.text(this._param.getNoSelectionText ? this._param.getNoSelectionText() : this._param.noSelectionText);
            return;
        }

        if (this._param.getTitleDescription) {
            const items = this._param.checkable ? this._list.checkedItems() : this._list.selectedItems();
            const titleDescription = this._param.getTitleDescription(items);
            if (this._param.titleDescriptionIsHtml) {
                this._titleControl.html(titleDescription);
            } else {
                this._titleControl.text(titleDescription);
            }
        } else {
            const text = this._param.checkable ? this._list.getCheckedItemsDescription() : this._list.getSelectedItemsDescription();
            this._titleControl.text(selectedItems && selectedItems.length > 0 ? text : '');
        }
    }

    getTitle() {
        return this._titleControl.text();
    }

    getValue() {
        return this._param.checkable ? this._list.checkedItems() : this._list.selectedItems();
    }

    setValue(values) {
        this.selectedItems(values);
    }

    getFirstOrDefaultId() {
        const selectedValues = this.getValue();
        if (selectedValues && selectedValues.length > 0) {
            return selectedValues[0].id;
        }

        return null;
    }

    selectedItems(selectedItems?, force?) {
        const results = this._param.checkable && !force ? this._list.checkedItems(selectedItems) : this._list.selectedItems(selectedItems);

        if (selectedItems != undefined) {
            this._lastSelectedItems = selectedItems;
            this.setTitle(selectedItems);
            this.validate();
        }
        return results;
    }

    checkedItems(checkedItems?) {
        const results = this._list.checkedItems(checkedItems);

        if (checkedItems != undefined) {
            this._lastSelectedItems = checkedItems;
            this.setTitle(checkedItems);
            this.validate();
        }
        return results;
    }

    validate() {
        const value = this.getValue();
        let state = true;
        if ($.isFunction(this._param.validator)) {
            state = this._param.validator(value);
        }
        this.onValidate(state);

        return state;
    }

    setExcludeMode(enabled: boolean): void {
        this._list.setExcludeMode(enabled);
    }

    // WTF 2 setItems
    // setItems (items) {
    //
    //     var result = this._list.items(items);
    //     if (items != undefined && this._popup.isVisible) {
    //         this._list.adjust(this._getWidth(), this._getHeight());
    //         this._popup.show();
    //     }
    //
    //     return result;
    // };

    setItems(items) {
        return this.items(items);
    }

    setError(msg) {
        this._root.addClass('invalid drop-down-invalid');
        this._root.attr('title', msg);
    }

    removeError() {
        this._root.removeClass('invalid drop-down-invalid');
        this._root.attr('title', '');
    }

    getControl() {
        return this._root;
    }

    getContent() {
        return this._content;
    }

    destroy() {
        this.onValidate.clear();
        this.onChange.clear();

        safeDestroys(this._list, this._content, this._titleControl, this._popup);

        super.destroy();
        destroyEvents(this);
    }

    close() {
        this.removeContent();
    }

    _adjustPopup() {
        if (this._popup.isVisible) {
            const w = this.getWidth();
            const h = this.getHeight();
            this._list.adjust(w, h);
            this._popup.getControl().css({ width: 'auto', height: 'auto' });
            this._popup.show(null, null, w, h);
        }
    }

    appendItems(items) {
        const result = this._list.appendItems(items);
        this._adjustPopup();
        this._onAppendItems();
        return result;
    }

    prependItems(items) {
        const result = this._list.prependItems(items);
        this._adjustPopup();
        return result;
    }

    insertItem(index, item) {
        const result = this._list.insertItem(index, item);
        this._adjustPopup();
        return result;
    }

    updateItem(id, item) {
        const res = this._list.updateItem(id, item);
        this.setTitle(this.selectedItems());
        return res;
    }

    removeItems(comparer) {
        const result = this._list.removeItems(comparer);
        this._adjustPopup();
        return result;
    }

    items(items?) {
        const result = this._list.items(items);
        this._adjustPopup();
        return result;
    }

    disable() {
        this._root.addClass('disabled');
        this._enabled = false;
    }

    enable(flag?) {
        if (flag == null) {
            flag = true;
        }
        if (flag) {
            this._root.removeClass('disabled');
            this._enabled = true;
        } else {
            this.disable();
        }
    }

    readonly(data) {
        this._root.toggleClass('readonly-dropdown', data === true);

        return BaseControl.prototype.readonly.call(this, data);
    }

    scrollToItem(item) {
        if (this._isContentVisible) {
            return this._list.scrollToItem(item);
        } else {
            this._scrollTo = item;
        }
    }

    setNodesVisibility() {
        this._list.setNodesVisibility();
    }

    selectItemsById(...ids: (string | number)[]): any[];
    selectItemsById(ids: (string | number)[]): any[];
    selectItemsById(...args: (string | number)[] | [(string | number)[]]): any[] {
        const ids = Array.isArray(args[0]) ? args[0] : ([...args] as (string | number)[]);
        const set = new Set(ids);
        const itemsForSelect = this.items().filter(item => item.id != null && set.has(item.id));
        this.selectedItems(itemsForSelect);
        return itemsForSelect;
    }

    getSelectedIds(): any[] {
        return this.selectedItems().map(_.property('id'));
    }

    onItemClick(fn) {
        if ($.isFunction(fn)) {
            this._onItemClick = fn;
        }
    }

    onAppendItems(fn) {
        if ($.isFunction(fn)) {
            this._onAppendItems = fn;
        }
    }

    onShowContent(fn) {
        if ($.isFunction(fn)) {
            this._onShowContent = fn;
        }
    }

    onRemoveContent(fn) {
        if ($.isFunction(fn)) {
            this._onRemoveContent = fn;
        }
    }

    findNodeById(id) {
        return this._list.findNodeById(id);
    }

    findItem(comparer) {
        return this._list.findItem(comparer);
    }

    findItemById(id) {
        return this._list.findItemById(id);
    }

    getNodes() {
        return this._list.nodes();
    }

    getItemHeight() {
        return this._list.getItemHeight();
    }

    getItemsCount() {
        return this._list.getItemsCount();
    }

    getContentHeight() {
        return this._list.getContentHeight();
    }

    selectNextItem() {
        return this._list.selectNextItem();
    }

    selectPrevItem() {
        return this._list.selectPrevItem();
    }

    setState(enabled) {
        if (enabled) {
            this.enable();
        } else {
            this.disable();
        }
    }

    getList() {
        return this._list;
    }

    startSpinner() {
        this._popup.getControl().spinnerStart({ isSmall: true });
    }

    stopSpinner() {
        this._popup.getControl().spinnerStop();
    }
}

interface IDropDown extends IValueChange<{ id: any; name: string; [key: string]: any }[]> {
    getContent(): JQuery;

    disable();

    enable(enabled?: boolean);

    state(enabled?: boolean);

    items(items?: any[]): any[];

    setItems(items: any[]);

    appendItems(items: any[]);

    selectedItems(items?: any[], force?: boolean);

    checkedItems(items?: any[]): any[];

    onAppendItems(fn: IHandler);

    onItemClick(handler: (item: any) => void);

    findItemById(id: any): any;

    getNodes(): IBaseMenuNode[];

    getItemHeight(): number;

    getItemsCount(): number;

    getContentHeight(): number;

    getValue(): any[];

    //        setValue(value);
    getFirstOrDefaultId(): any;

    getList(): List;

    setError(errorMessage: string);

    removeError();

    validate(): boolean;

    onValidate(handler: (isValid: boolean) => void);

    startSpinner();

    stopSpinner();

    onRemoveContent(fn);

    onShowContent(fn);

    destroy(): void;

    close(): void;

    processSelection(): void;

    selectItemsById(ids: (string | number)[]): any[];

    selectItemsById(...ids: (string | number)[]): any[];

    setTitle(items); // AY:  make public for viewEditor.ts
    findItem(compare: (item: any) => boolean): any;

    findNodeById(id: string): IBaseMenuNode;

    selectNextItem(): void;

    setNodesVisibility(): void;
}
